import {
  TransactionOrder,
  OrderPreviewDataResponseV4,
  TransactionResourceV2,
} from '@olxeu-a2b/core/typings';
import { ParsedTransaction } from '@olxeu-a2b/ui/typings/transaction';
import connectionQuality from './connectionQuality';

// TransactionResourceV2 extends TransactionResource
export declare type Transaction = TransactionResourceV2 & { id: string };
export type LowestAvailableShippingPrice = {
  discountedAmount: null | number;
  priceAmount: number;
  showDiscountedShipmentPrice: boolean;
  discountType?: string;
  amountBeforeDiscount?: number;
  currency: string | null;
  lowestPriceOperatorKey: string | null;
};

const isInt = (n: number): boolean => n % 1 === 0;
const SHIPMENT_ = 'SHIPMENT_';
const FEE_SERVICE = 'FEE_SERVICE';

export const parsePrice = (priceInCents?: number): string => {
  if (priceInCents === undefined) {
    return '';
  }
  const parsed = priceInCents / 100;
  return isInt(parsed) ? parsed.toString() : parsed.toFixed(2);
};

export const getPriceComponentAmount = (order: TransactionOrder, componentCode: string): string =>
  parsePrice(
    order.price.components.find((comp: { code: string }) => comp.code === componentCode)?.value
      .amount,
  );

export const getLowestAvailableShippingPriceV4 = (
  priceData: OrderPreviewDataResponseV4,
): LowestAvailableShippingPrice => {
  if (!priceData || !priceData.components) {
    return {
      discountedAmount: null,
      priceAmount: null,
      showDiscountedShipmentPrice: false,
      amountBeforeDiscount: null,
      currency: null,
      lowestPriceOperatorKey: null,
    };
  }

  const shipmentPrices = priceData.components.filter(component =>
    component.code.includes(SHIPMENT_),
  );

  const sortedShipmentPricesByDiscountedAmount = shipmentPrices
    .filter(component => component.value.discountedAmount !== null)
    .sort((previous, next) => previous.value.discountedAmount - next.value.discountedAmount);
  if (sortedShipmentPricesByDiscountedAmount.length > 0) {
    const lowestPriceOperatorKey = sortedShipmentPricesByDiscountedAmount[0].code
      .substring(SHIPMENT_.length)
      .toLowerCase();
    const {
      discountType,
      discountedAmount: discountedAmountInCents,
      amount,
      amountBeforeDiscount: amountBeforeDiscountInCents,
      currency,
    } = sortedShipmentPricesByDiscountedAmount[0].value;
    const discountedAmount = discountedAmountInCents / 100;
    const priceAmount = amount / 100;
    const amountBeforeDiscount = amountBeforeDiscountInCents / 100;
    return {
      discountedAmount,
      discountType,
      priceAmount,
      amountBeforeDiscount,
      showDiscountedShipmentPrice: true,
      currency,
      lowestPriceOperatorKey,
    };
  }

  const sortedPriceValuesByShipmentPrice = shipmentPrices.sort(
    (previous, next) => previous.value.amount - next.value.amount,
  );

  const { amount: amountShipmentPrice, currency: currencyShipmentPrice } =
    sortedPriceValuesByShipmentPrice[0].value;
  const lowestPriceOperatorKeyShipmentPrice = sortedPriceValuesByShipmentPrice[0].code
    .substring(SHIPMENT_.length)
    .toLowerCase();
  return {
    discountedAmount: null,
    priceAmount: amountShipmentPrice / 100,
    currency: currencyShipmentPrice,
    showDiscountedShipmentPrice: false,
    lowestPriceOperatorKey: lowestPriceOperatorKeyShipmentPrice,
  };
};

export const getServiceFee = (priceData: OrderPreviewDataResponseV4) => {
  if (!priceData || !priceData.components) {
    return null;
  }

  const serviceFee = priceData.components.find(component => component.code.includes(FEE_SERVICE));

  if (serviceFee) {
    return {
      serviceFee: serviceFee.value.amount / 100,
      discountedServiceFee:
        serviceFee.value.discountedAmount === null ? null : serviceFee.value.discountedAmount / 100,
      discountType: serviceFee.value.discountType,
    };
  }
  return null;
};

export const parsePriceToInt = (price: number) => {
  if (typeof price !== 'number') return null;
  return Math.floor(price * 100);
};

export const getAvailableShipmentOperatorsV4 = (pricePreview: OrderPreviewDataResponseV4) =>
  pricePreview.components
    .filter(component => component.code.includes(SHIPMENT_))
    .map(operator => operator.code.substring(SHIPMENT_.length).toLowerCase());

export const parseTransaction = (transaction: Transaction): ParsedTransaction => {
  const { id, attributes } = transaction;
  const imageQuality = connectionQuality('512x512', '128x128');

  return {
    id,
    status: attributes.status,
    statusReason: attributes.statusReason,
    statusChangeTime: attributes.statusChangeTime,
    paymentMethod: attributes.paymentMethod,
    ad: {
      ...attributes.ad,
      image: attributes.ad?.image?.replace('{width}x{height}', imageQuality) || null,
    },
    order: {
      id: attributes.order.id,
      createdAt: new Date(attributes.order.createdAt),
      shipmentPriceAmount: getPriceComponentAmount(attributes.order, 'shipment-price'),
      itemPriceAmount: getPriceComponentAmount(attributes.order, 'item-price'),
      sellerTakeRatePriceAmount: getPriceComponentAmount(
        attributes.order,
        'seller-take-rate-price',
      ),
      price: attributes.order?.price,
    },
    confirmationDeadline: attributes.order?.confirmationDeadline.days,
    shipmentDeadline: attributes.shipment?.packagePostingDeadline?.days,
    shipment: attributes.shipment,
    buyer: attributes.buyer,
    seller: attributes.seller,
    packingPoint: attributes.packingPoint,
    destinationPoint: attributes.destinationPoint,
    pickUpWindow: attributes.pickUpWindow,
    deliveryType: attributes.destinationAddress || attributes.packingAddress ? 'D2D' : 'P2P',
    destinationAddress: attributes.destinationAddress,
    deliveryProductId: attributes.deliveryProductId?.split('_')[0],
    packingAddress: attributes.packingAddress,
  };
};
